import isEqualWith from 'lodash/isEqualWith'
import { useEffect, useMemo, useRef } from 'react'

import { LIVE_PREVIEW_DEBOUNCE_TIME } from '@features/invoices/constants/livePreviewDebounceTime'
import { getCustomTemplateSettingsPayload } from '@features/invoicingSettings/utils/getCustomTemplateSettingsPayload'

import { useDebouncedValue, useWatch } from '../../../../../../../../../hooks'
import { useInvoicePreviewAppearanceForm } from '../elements/InvoicePreviewAppearanceSettings/contexts/invoicePreviewAppearanceFormContext'
import { InvoicePreviewAppearanceProps } from '../types/invoicePreviewAppearanceProps'

interface AppearanceFormValues {
  logoId: string | null | undefined
  logoUrl: string | null | undefined
  shouldApplyTemplateColorToColumnHeaders: boolean | undefined
  shouldApplyTemplateColorToDividers: boolean | undefined
  shouldApplyTemplateColorToHeader: boolean | undefined
  shouldApplyTemplateColorToTotal: boolean | undefined
  templateColor: string | undefined
  templateFont: string | undefined
}

export const useAppearanceLivePreview = ({
  documentType,
  updateInvoicePreviewAppearance,
}: InvoicePreviewAppearanceProps) => {
  const { hasFailedToLoad, isLoading, logoAndBrandingSettingsForm } = useInvoicePreviewAppearanceForm()
  const { control } = logoAndBrandingSettingsForm

  const [
    logoId,
    logoUrl,
    shouldApplyTemplateColorToColumnHeaders,
    shouldApplyTemplateColorToDividers,
    shouldApplyTemplateColorToHeader,
    shouldApplyTemplateColorToTotal,
    templateColor,
    templateFont,
  ] = useWatch({
    control,
    name: [
      'logoId',
      'logoUrl',
      'shouldApplyTemplateColorToColumnHeaders',
      'shouldApplyTemplateColorToDividers',
      'shouldApplyTemplateColorToHeader',
      'shouldApplyTemplateColorToTotal',
      'templateColor',
      'templateFont',
    ],
  })

  const formValues = useMemo<AppearanceFormValues>(
    () => ({
      logoId,
      logoUrl,
      shouldApplyTemplateColorToColumnHeaders,
      shouldApplyTemplateColorToDividers,
      shouldApplyTemplateColorToHeader,
      shouldApplyTemplateColorToTotal,
      templateColor,
      templateFont,
    }),
    [
      logoId,
      logoUrl,
      shouldApplyTemplateColorToColumnHeaders,
      shouldApplyTemplateColorToDividers,
      shouldApplyTemplateColorToHeader,
      shouldApplyTemplateColorToTotal,
      templateColor,
      templateFont,
    ],
  )

  const formValuesRef = useRef(formValues)
  formValuesRef.current = formValues

  const debouncedFormValues = useDebouncedValue(formValues, LIVE_PREVIEW_DEBOUNCE_TIME)

  const previousFormValuesRef = useRef<Partial<AppearanceFormValues>>({})

  useEffect(() => {
    const currentFormValues = formValuesRef.current
    const hasFormValuesChanged = !isEqualWith(currentFormValues, previousFormValuesRef.current)

    // A failed load leaves the form on its defaults, which must not replace the saved branding.
    if (!isLoading && !hasFailedToLoad && hasFormValuesChanged) {
      const { logoId: currentLogoId, logoUrl: currentLogoUrl } = currentFormValues
      const logoOverride = { id: currentLogoId ?? '', ...(currentLogoUrl ? { content: currentLogoUrl } : {}) }

      updateInvoicePreviewAppearance(
        getCustomTemplateSettingsPayload({
          templateColor: currentFormValues.templateColor,
          templateFont: currentFormValues.templateFont,
          shouldApplyTemplateColorToColumnHeaders: currentFormValues.shouldApplyTemplateColorToColumnHeaders ?? true,
          shouldApplyTemplateColorToDividers: currentFormValues.shouldApplyTemplateColorToDividers ?? true,
          shouldApplyTemplateColorToHeader: currentFormValues.shouldApplyTemplateColorToHeader ?? true,
          shouldApplyTemplateColorToTotal: currentFormValues.shouldApplyTemplateColorToTotal ?? true,
        }),
        logoOverride,
      )

      previousFormValuesRef.current = currentFormValues
    }
  }, [
    debouncedFormValues,
    documentType,
    formValuesRef,
    hasFailedToLoad,
    isLoading,
    previousFormValuesRef,
    updateInvoicePreviewAppearance,
  ])
}
